import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import Scene from "./Scene";
import Nav from "./overlays/Nav";
import HeroOverlay from "./overlays/HeroOverlay";
import NoiseOverlay from "./overlays/NoiseOverlay";
import ProductOverlay from "./overlays/ProductOverlay";
import PromiseOverlay from "./overlays/PromiseOverlay";
import FooterOverlay from "./overlays/FooterOverlay";
import ProgressRail from "./overlays/ProgressRail";
import Marginalia from "./Marginalia";
import AdminPanel from "./AdminPanel";
import AnalyticsConsent from "./AnalyticsConsent";
import { useScrollController } from "./useScrollController";
import { fetchConfig, fetchWaitlistCount } from "./config";
import { COPY, LangContext } from "./i18n";
import { Toaster } from "@/components/ui/sonner";
import { trackEvent } from "./analytics";

// Grown from 760 when the promise act was added — two more beats need room,
// otherwise every existing statement gets a shorter hold.
const TOTAL_VH = 940;

export default function Landing() {
  const pageRef = useRef(null);
  const [lang, setLang] = useState("en");
  const [isPostLaunch, setIsPostLaunch] = useState(false);
  const [count, setCount] = useState(null);

  const refreshCount = () => fetchWaitlistCount().then(setCount);
  useEffect(() => {
    fetchConfig().then(setIsPostLaunch);
    refreshCount();
  }, []);

  // Keep <html lang> in sync with the toggle so screen readers and search
  // engines get the right language for the copy that's actually on screen.
  useEffect(() => {
    document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-CA";
  }, [lang]);

  useScrollController(pageRef);

  const changeLanguage = useCallback((nextLang) => {
    setLang((currentLang) => {
      if (currentLang !== nextLang) {
        trackEvent("language_change", { from_language: currentLang, to_language: nextLang });
      }
      return nextLang;
    });
  }, []);

  const ctx = useMemo(() => ({ lang, setLang: changeLanguage, t: COPY[lang] }), [lang, changeLanguage]);

  return (
    <LangContext.Provider value={ctx}>
      <div style={{ background: "#0A0A0A" }}>
        <div className="fixed inset-0 z-0">
          <Scene />
        </div>

        <Nav />
        <Marginalia />
        <div className="bb-overlay">
          <HeroOverlay />
          <NoiseOverlay />
          <ProductOverlay />
          <PromiseOverlay />
          <FooterOverlay isPostLaunch={isPostLaunch} count={count} onJoined={refreshCount} />
        </div>
        <ProgressRail />

        <div className="bb-grain" />
        <Toaster position="bottom-center" />
        <AnalyticsConsent />
        <AdminPanel isPostLaunch={isPostLaunch} onChange={setIsPostLaunch} />

        <div ref={pageRef} style={{ height: `${TOTAL_VH}vh` }} aria-hidden="true" />
      </div>
    </LangContext.Provider>
  );
}
